import type { ApiGet } from "./apiFetch.js";

// POST /api/sets/batch answers 200 with one result per set
// (apps/api/src/lib/setBatchResults.ts): a replay reads them row by row (#286).

/** Statuses that mean the set is on the server: written now, or already. */
const STORED = new Set(["ok", "duplicate_id"]);

/** Statuses the same payload gets again on every send. */
const REFUSED = new Set(["invalid_payload", "invalid_tag", "unknown_exercise"]);

/** Sets refused for good: the same payload gets the same answer. Duck-typed
 *  on `name`, like TagCapError. */
export class SetsRefusedError extends Error {
  readonly statuses: string[];
  constructor(statuses: string[]) {
    super(`sets_refused_${statuses.join("_")}`);
    this.name = "SetsRefusedError";
    this.statuses = statuses;
  }
}

export const isSetsRefusedError = (err: unknown): err is SetsRefusedError =>
  err instanceof Error && err.name === "SetsRefusedError";

/** Message of the plain Errors `assertSetsStored` throws: sets not stored this
 *  time, saving again may store them. */
const UNREADABLE = "sets_batch_unreadable";
const NOT_STORED = "sets_not_stored_";

export const isSetsNotStoredError = (err: unknown): err is Error =>
  err instanceof Error &&
  (err.message === UNREADABLE || err.message.startsWith(NOT_STORED));

/** One batch answer and the number of sets it was sent. */
export interface SetsAnswer {
  resp: unknown;
  count: number;
}

const statusesOf = ({ resp, count }: SetsAnswer): string[] | null => {
  const results =
    resp && typeof resp === "object"
      ? (resp as { results?: unknown }).results
      : undefined;
  if (!Array.isArray(results) || results.length !== count) return null;
  return results.map((r: unknown) =>
    r && typeof r === "object" ? String((r as { status?: unknown }).status) : "",
  );
};

/** Throws unless every set of every batch is stored. Read once all the
 *  batches went, so a refusal in one never keeps the next from being sent:
 *  an unreadable answer is a plain Error (retried), then a refusal for good
 *  is SetsRefusedError, then any other set not stored is a plain Error. */
export function assertSetsStored(answers: readonly SetsAnswer[]): void {
  const read = answers.map(statusesOf);
  if (read.some((s) => s === null)) throw new Error(UNREADABLE);
  const statuses = read.flatMap((s) => s ?? []);
  const refused = statuses.filter((s) => REFUSED.has(s));
  if (refused.length > 0) throw new SetsRefusedError([...new Set(refused)]);
  const missing = statuses.filter((s) => !STORED.has(s));
  if (missing.length > 0) {
    throw new Error(`${NOT_STORED}${[...new Set(missing)].join("_")}`);
  }
}

/** The fields that tell one set of a session from another without its id:
 *  exercise, entry position and time, all three from splitWorkout. */
const setKey = (set: unknown): string => {
  const s = (set && typeof set === "object" ? set : {}) as {
    exerciseSlug?: unknown;
    performedAt?: unknown;
    tags?: unknown;
  };
  const entry = Array.isArray(s.tags)
    ? s.tags.find((t) => typeof t === "string" && t.startsWith("entry:"))
    : undefined;
  return `${String(s.exerciseSlug)}|${Date.parse(String(s.performedAt))}|${String(entry)}`;
};

/**
 * The sets of `sets` the server does not hold yet for session `sessionId`.
 * For a row an older app queued (#286): it may have stored these sets under
 * ids the server chose, then lost the answer, so the ids minted since cannot
 * dedup them. Each stored set matches one of ours at most. "The session has
 * sets, so it is done" is not enough: the older app saved in chunks of 100,
 * and a save cut after the first chunk would lose the later ones in silence.
 * Throws when the session cannot be read: the replay waits rather than send
 * them twice.
 */
export async function setsNotStored<S>(
  apiGet: ApiGet,
  sessionId: string,
  sets: readonly S[],
): Promise<S[]> {
  const resp = await apiGet<unknown>(
    `/api/sessions/${encodeURIComponent(sessionId)}?include=sets`,
  );
  const stored =
    resp && typeof resp === "object" ? (resp as { sets?: unknown }).sets : undefined;
  if (!Array.isArray(stored)) throw new Error("session_sets_unreadable");
  const left = new Map<string, number>();
  for (const s of stored) {
    const key = setKey(s);
    left.set(key, (left.get(key) ?? 0) + 1);
  }
  return sets.filter((s) => {
    const key = setKey(s);
    const n = left.get(key) ?? 0;
    if (n > 0) left.set(key, n - 1);
    return n === 0;
  });
}
